import React, { useEffect, useState } from "react";
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Database,
} from "lucide-react";
import { useImportStore } from "../../store/useImportStore";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { formatDateTime, formatFileSize } from "../../utils/formatters";
import { calculateEstimatedTime } from "../../utils/importUtils";

export const ImportProgress: React.FC = () => {
  const { currentJob, fetchImportJob, setCurrentStep, currentFile } =
    useImportStore();

  const [pollingInterval, setPollingInterval] = useState<
    NodeJS.Timeout | undefined
  >(undefined);

  // Poll for job status updates
  useEffect(() => {
    if (!currentJob || currentJob.status !== "processing") {
      return;
    }

    const interval = setInterval(async () => {
      try {
        await fetchImportJob(currentJob.id);
      } catch (error) {
        console.error("Error polling job status:", error);
      }
    }, 2000); // Poll every 2 seconds

    setPollingInterval(interval);

    return () => {
      clearInterval(interval);
    };
  }, [currentJob?.id, fetchImportJob]); // Only depend on job ID, not the whole job object

  // Add a separate effect to handle status transitions
  useEffect(() => {
    if (
      currentJob &&
      (currentJob.status === "completed" || currentJob.status === "failed")
    ) {
      // Clear any existing polling
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(undefined);
      }

      // Auto-transition to results after a short delay for completed jobs
      if (currentJob.status === "completed") {
        const timer = setTimeout(() => {
          setCurrentStep("results");
        }, 1500); // 1.5 second delay to show completion state

        return () => clearTimeout(timer);
      }
    }
  }, [currentJob?.status, setCurrentStep, pollingInterval]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  if (!currentJob || !currentFile) {
    return (
      <div className="p-8">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No Import Job Found
          </h2>
          <p className="text-gray-600 mb-6">
            Unable to find the current import job details.
          </p>
        </div>
      </div>
    );
  }

  const getStatusIcon = () => {
    switch (currentJob.status) {
      case "processing":
        return <LoadingSpinner size="lg" className="text-blue-500" />;
      case "completed":
        return <CheckCircle className="w-12 h-12 text-green-500" />;
      case "failed":
        return <XCircle className="w-12 h-12 text-red-500" />;
      case "cancelled":
        return <XCircle className="w-12 h-12 text-gray-500" />;
      default:
        return <Clock className="w-12 h-12 text-gray-500" />;
    }
  };

  const getStatusMessage = () => {
    switch (currentJob.status) {
      case "pending":
        return "Import job is queued and waiting to start...";
      case "processing":
        return "Processing your CSV file and importing GPS data...";
      case "completed":
        return "Import completed successfully!";
      case "failed":
        return "Import failed due to an error.";
      case "cancelled":
        return "Import was cancelled.";
      default:
        return "Unknown status";
    }
  };

  const progressPercentage = currentJob.progress_percentage || 0;
  const isProcessing = currentJob.status === "processing";
  const isCompleted = currentJob.status === "completed";
  const isFailed = currentJob.status === "failed";

  return (
    <div className="p-8">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">{getStatusIcon()}</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {isProcessing && "Importing GPS Data..."}
          {isCompleted && "Import Completed!"}
          {isFailed && "Import Failed"}
          {currentJob.status === "pending" && "Import Starting..."}
          {currentJob.status === "cancelled" && "Import Cancelled"}
        </h2>
        <p className="text-gray-600">{getStatusMessage()}</p>
      </div>

      {/* File Information */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2" />
          File Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-600">File Name</p>
            <p className="text-lg text-gray-900 truncate">{currentFile.name}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">File Size</p>
            <p className="text-lg text-gray-900">
              {formatFileSize(currentJob.file_size)}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Total Rows</p>
            <p className="text-lg text-gray-900">
              {currentJob.total_rows > 0
                ? currentJob.total_rows.toLocaleString()
                : "Calculating..."}
            </p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      {(isProcessing || isCompleted) && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900">Progress</h3>
            <span className="text-sm font-medium text-gray-600">
              {progressPercentage.toFixed(1)}%
            </span>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
            <div
              className={`h-4 rounded-full transition-all duration-300 ${
                isCompleted
                  ? "bg-green-500"
                  : isFailed
                  ? "bg-red-500"
                  : "bg-blue-500"
              }`}
              style={{ width: `${Math.min(progressPercentage, 100)}%` }}
            />
          </div>

          {/* Processing Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-600">Processed</p>
              <p className="text-xl font-bold text-gray-900">
                {currentJob.processed_rows.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500">
                of {currentJob.total_rows.toLocaleString()} rows
              </p>
            </div>

            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-sm font-medium text-green-800">Successful</p>
              <p className="text-xl font-bold text-green-900">
                {currentJob.successful_rows.toLocaleString()}
              </p>
              <p className="text-xs text-green-600">
                {currentJob.total_rows > 0
                  ? `${(
                      (currentJob.successful_rows / currentJob.total_rows) *
                      100
                    ).toFixed(1)}%`
                  : "0%"}
              </p>
            </div>

            {currentJob.failed_rows > 0 && (
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p className="text-sm font-medium text-red-800">Failed</p>
                <p className="text-xl font-bold text-red-900">
                  {currentJob.failed_rows.toLocaleString()}
                </p>
                <p className="text-xs text-red-600">
                  {currentJob.total_rows > 0
                    ? `${(
                        (currentJob.failed_rows / currentJob.total_rows) *
                        100
                      ).toFixed(1)}%`
                    : "0%"}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Processing Details */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Database className="w-5 h-5 mr-2" />
          Processing Details
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-600">Job ID</span>
            <span className="text-sm text-gray-900 font-mono">
              {currentJob.id}
            </span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-600">Status</span>
            <span
              className={`text-sm px-2 py-1 rounded-full font-medium ${
                currentJob.status === "processing"
                  ? "bg-blue-100 text-blue-800"
                  : currentJob.status === "completed"
                  ? "bg-green-100 text-green-800"
                  : currentJob.status === "failed"
                  ? "bg-red-100 text-red-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {currentJob.status.charAt(0).toUpperCase() +
                currentJob.status.slice(1)}
            </span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-600">Started</span>
            <span className="text-sm text-gray-900">
              {currentJob.start_time
                ? formatDateTime(currentJob.start_time)
                : "-"}
            </span>
          </div>

          {currentJob.end_time && (
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-600">
                Completed
              </span>
              <span className="text-sm text-gray-900">
                {formatDateTime(currentJob.end_time)}
              </span>
            </div>
          )}

          {isProcessing && (
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600">
                Estimated Time Remaining
              </span>
              <span className="text-sm text-gray-900">
                {calculateEstimatedTime(currentJob)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {currentJob.error_message && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <h4 className="text-sm font-medium text-red-800 mb-2">
            Error Details
          </h4>
          <p className="text-sm text-red-700">{currentJob.error_message}</p>
        </div>
      )}

      {/* Real-time Updates Indicator */}
      {isProcessing && (
        <div className="text-center">
          <p className="text-sm text-gray-500 flex items-center justify-center">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2 inline-block" />
            Live updates every 2 seconds
          </p>
        </div>
      )}

      {/* Processing Tips */}
      {isProcessing && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-800 mb-2">
            Processing Tips
          </h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>
              • Large files may take several minutes to process completely
            </li>
            <li>
              • You can safely close this page - the import will continue in the
              background
            </li>
            <li>• Check the import history to view the final results later</li>
          </ul>
        </div>
      )}
    </div>
  );
};
